import React, { ReactElement, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { delay } from "helpers/app";

import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";

import { clusterAppSteps } from "constants/cluster";
import { domainValidation } from "constants/regexp";

import SectionsBreadCrumbs from "components/shared/breadCrumbs/SectionsBreadCrumbs";
import LoadingComponent from "components/shared/loaders/LoadingComponent";
import AppModal from "components/shared/modal/AppModal";
import UserWaiting from "components/shared/progressBar/UserWaiting";

import { postCreateWebsite } from "redux/handlers/clusterHandler";
import { getRepositorys, getTypeVersions } from "redux/handlers/dockerHandler";
import { getServerByType } from "redux/handlers/utilityHandler";

import { useAppDispatch } from "hooks/reduxHook";

import { IDockerRepo, IDockerTypeVersions } from "types/api/dockerApiInterface";
import { IServerByTypeAPI } from "types/api/serversApiInterface";

import ClusterAppCustomComponent from "../CusterAppCustomComponent";

import ClusterAppRecap from "./ClusterAppRecap";

const ClusterAppCustomWizard = ({ type }: { type: string }): ReactElement => {
 const intl = useIntl();
 const navigate = useNavigate();
 const dispatch = useAppDispatch();
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("lg"));
 const params = useParams<{ id: string }>();

 const [activeStep, setActiveStep] = useState<number>(0);
 const [openModal, setOpenModal] = useState<boolean>(false);
 const [isLoading, setIsLoading] = useState<boolean>(true);
 const [loading, setLoading] = useState<boolean>(false);
 const [servers, setServers] = useState<Array<IServerByTypeAPI>>([]);
 const [appVers, setAppVers] = useState<IDockerTypeVersions | null>(null);
 const [repository, setRepository] = useState<Array<IDockerRepo>>([]);

 useEffect(() => {
  (async () => {
   setAppVers(await dispatch(getTypeVersions()));
   setRepository(await dispatch(getRepositorys()));
   setServers(await dispatch(getServerByType("container-app")));
   setValue("app_type", type);
   setIsLoading(false);
  })();
 }, []);

 const { control, formState, reset, watch, setValue } = useForm({
  defaultValues: {
   site_name: "",
   app_type: "",
   app_version: "",
   replicas: 1
  }
 });

 const handleOpen = () => setOpenModal(true);
 const handleClose = () => setOpenModal(false);
 const handleReset = () => {
  reset();
  setActiveStep(0);
 };

 const selectRepo = (vers: string): string => {
  if (vers.includes("tomcat")) {
   return repository.find((element) => element.name.includes("tomcat"))?.name || "";
  } else if (vers.includes("nginx")) {
   return repository.find((element) => element.name.includes("nginx"))?.name || "";
  } else if (vers.includes("apache")) {
   return repository.find((element) => element.name.includes("apache"))?.name || "";
  } else if (vers.includes("odoo")) {
   return repository.find((element) => element.name.includes("odoo"))?.name || "";
  } else {
   return repository[0].name;
  }
 };

 const handleConfirm = async () => {
  setLoading(true);
  await dispatch(
   postCreateWebsite(
    Number(params.id),
    watch("site_name"),
    watch("app_version"),
    selectRepo(watch("app_version")),
    watch("replicas")
   )
  );
  await delay(3000);
  setLoading(false);
  handleClose();
  navigate(`/servers/cluster/${Number(params.id)}`);
  handleReset();
 };

 const checkActiveStep = (index: number) => {
  switch (index) {
   case 0:
    return watch("site_name") !== "" && domainValidation.test(watch("site_name"));
   case 1:
    return watch("app_version") !== "";
   case 2:
    return watch("replicas") > 0;
   case 3:
    return watch("app_type") === "";
   default:
    return false;
  }
 };

 const switchText = (): string => {
  switch (type) {
   case "php":
    return "cluster.add.createPhpApp";
   default:
    return "cluster.add.createCustomApp";
  }
 };

 const switchIcon = (): string => {
  switch (type) {
   case "php":
    return "PhpIcon";
   default:
    return "CustomIcon";
  }
 };

 const handleSetValue = (type: string, value: string | number) => {
  switch (type) {
   case "site_name":
    setValue("site_name", value as string);
    break;
   case "app_version":
    setValue("app_version", value as string);
    break;
   case "replicas":
    setValue("replicas", value as number);
    break;
   default:
    break;
  }
 };

 return !isLoading ? (
  <Container maxWidth={false} sx={{ mx: 2, width: "auto" }}>
   <Stack mt={2}>
    <SectionsBreadCrumbs
     links={[
      { name: "home", url: "/" },
      { name: "app", url: "/app" },
      { name: "sites", url: "/app/sites" },
      { name: "add", url: "/app/sites/add" }
     ]}
    />
   </Stack>
   <Grid container spacing={3} sx={{ mt: 3 }}>
    <Grid xs={desktopViewPort ? 3 : 12} sx={{ textAlign: "left" }}>
     <Stack sx={{ position: "sticky", top: 20 }}>
      <Typography variant="unboundedTitle" fontSize={18} fontWeight="bold">
       <FormattedMessage id={switchText()} />
      </Typography>
      {desktopViewPort && (
       <Stepper activeStep={activeStep} orientation="vertical">
        {clusterAppSteps.map((step, index) => (
         <Step key={step.label} completed={checkActiveStep(index)}>
          <StepLabel>
           <FormattedMessage id={step.label} />
          </StepLabel>
         </Step>
        ))}
       </Stepper>
      )}
     </Stack>
    </Grid>
    <Grid xs={desktopViewPort ? 6 : 12}>
     <ClusterAppCustomComponent
      control={control}
      formState={formState}
      appVers={appVers}
      watch={watch}
      handleOpen={handleOpen}
      handleReset={handleReset}
      setValue={(type, value) => handleSetValue(type, value)}
     />
    </Grid>
    <Grid xs={desktopViewPort ? 3 : 12}>
     <ClusterAppRecap
      server={servers.find((element) => element.id === Number(params.id))?.server_name || ""}
      site={watch("site_name")}
      type={watch("app_type")}
      version={watch("app_version")}
      icon={switchIcon()}
      replicas={Number(watch("replicas"))}
     />
    </Grid>
   </Grid>
   <AppModal
    open={openModal}
    close={handleClose}
    title={intl.formatMessage({ id: "cluster.add.createNewApp" })}
    handleClose={handleClose}
    disabled={loading}
    handleConfirm={handleConfirm}>
    <Stack>
     <FormattedMessage id="cluster.add.confirmAppCreation" />
    </Stack>
   </AppModal>
   <UserWaiting openLoader={loading} />
  </Container>
 ) : (
  <LoadingComponent section="cluster" />
 );
};

export default ClusterAppCustomWizard;
